import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { useTranslation } from "../../hooks/useTranslation";
import { TenantContext } from "../../contexts/TenantContext";
import { EVENTO_LIMITE, EVENTO_PIANI, apriPiani } from "../../utils/limitiPiano";
import ModalePiani, { alLimite, propostaPer, useDatiPiani } from "../wizards/custom/ModalePiani";

/**
 * In tutte le pagine: quando qualcosa si scontra con il piano, la finestra
 * dei piani (ModalePiani) con quelli che risolvono il limite. Arriva da due
 * parti: l'API che rifiuta (402, EVENTO_LIMITE dal client) e i pulsanti che a
 * limite pieno non aprono nemmeno il loro modulo (EVENTO_PIANI, vedi
 * useControlloLimite). A chi non puo' comprare (un collaboratore, chi e'
 * venduto offline, chi e' gia' sul piano piu' alto) resta l'avviso scritto
 * con il motivo; il collaboratore sa che deve chiedere al titolare.
 * Nell'onboarding le fonti oltre il piano le gestisce il wizard, con la sua
 * finestra.
 */
export default function AvvisoLimitePiano() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // Anche fuori dal provider (es. nei test del layout): senza, niente pulsante.
  const canManageBilling = Boolean(useContext(TenantContext)?.canManageBilling);
  const dati = useDatiPiani();
  const [richiesta, setRichiesta] = useState(null);
  const ultimo = useRef(0);
  const nelWizard = pathname.startsWith("/bots/new");

  const avvisoScritto = useCallback(
    (messaggio) => {
      // Una richiesta che ne fa partire altre (es. il wizard): un avviso solo.
      if (Date.now() - ultimo.current < 3000) return;
      ultimo.current = Date.now();
      toast({
        title: t("billing.limitReachedTitle"),
        description: canManageBilling ? messaggio : `${messaggio || ""} ${t("billing.limitAskOwner")}`.trim(),
        action: canManageBilling ? (
          <ToastAction altText={t("billing.limitSeePlans")} onClick={() => navigate("/billing")}>
            {t("billing.limitSeePlans")}
          </ToastAction>
        ) : undefined,
      });
    },
    [t, toast, navigate, canManageBilling]
  );

  useEffect(() => {
    const dalServer = (evento) => {
      const bisogno = evento.detail?.bisogno;
      if (nelWizard && bisogno?.limite === "sources_max") return;
      setRichiesta({ bisogno, messaggio: evento.detail?.message, dalServer: true });
    };
    const daUnPulsante = (evento) => setRichiesta({ bisogno: evento.detail, dalServer: false });
    window.addEventListener(EVENTO_LIMITE, dalServer);
    window.addEventListener(EVENTO_PIANI, daUnPulsante);
    return () => {
      window.removeEventListener(EVENTO_LIMITE, dalServer);
      window.removeEventListener(EVENTO_PIANI, daUnPulsante);
    };
  }, [nelWizard]);

  const proposta = richiesta ? propostaPer(richiesta.bisogno, dati) : null;

  // Niente piani da proporre: l'avviso scritto, se l'ha detto il server (un
  // pulsante apre la finestra solo quando ci sono piani, useControlloLimite).
  useEffect(() => {
    if (!richiesta || proposta || !dati.pronti) return;
    if (richiesta.dalServer) avvisoScritto(richiesta.messaggio);
    setRichiesta(null);
  }, [richiesta, proposta, dati.pronti, avvisoScritto]);

  if (!richiesta || !proposta) return null;
  return (
    <ModalePiani
      proposta={proposta}
      onScegli={(path) => {
        setRichiesta(null);
        navigate(path);
      }}
      onGratuito={() => setRichiesta(null)}
    />
  );
}

/**
 * Per i pulsanti che aprono un modulo (aggiungere una fonte, creare un
 * assistente, accendere un'app): `controlla(bisogno, azione)` da' la funzione
 * da chiamare al clic. A limite pieno, se ci sono piani da proporre, apre la
 * finestra dei piani al posto del modulo; altrimenti fa quello di sempre.
 * `serveIlPiano(bisogno)`: lo stesso controllo, per chi deve solo saperlo.
 */
export function useControlloLimite() {
  const dati = useDatiPiani();
  const serveIlPiano = (bisogno) => alLimite(bisogno, dati.consumi) && Boolean(propostaPer(bisogno, dati));
  const controlla =
    (bisogno, azione) =>
    (...argomenti) =>
      serveIlPiano(bisogno) ? apriPiani(bisogno) : azione(...argomenti);
  return { controlla, serveIlPiano, puoComprare: (bisogno) => Boolean(propostaPer(bisogno, dati)) };
}
